import { useEffect, useEffectEvent, useLayoutEffect, useRef, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

/**
 * Labeled modal dialog (blueprint §5 gate — docs/redesign/DESIGN_BLUEPRINT.md)
 * built on native <dialog>: the platform provides the focus trap,
 * Escape-to-close, top-layer rendering and focus restore — the pieces the
 * audited production `BottomSheet` lacked.
 * Every dialog must pass `labelledBy` pointing at its visible heading.
 *
 * Backdrop clicks close. Listeners are registered natively (not via JSX)
 * because the a11y linter bans click handlers on the non-interactive
 * <dialog> element — a false positive here: keyboard users close with
 * Escape, so the pointer-only backdrop needs no keyboard equivalent. A click
 * lands on the <dialog> element itself only when it hits the backdrop, since
 * the box is sized to its content.
 */
export function Dialog({
  open,
  onClose,
  labelledBy,
  className = '',
  children,
}: {
  open: boolean;
  onClose: () => void;
  labelledBy: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const handleCloseEvent = useEffectEvent(() => {
    // An owner-driven close has already synchronized state. Its later native
    // notification must not repeat the dismissal or clear a new opening.
    if (open) onClose();
  });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const handleClose = () => {
      // Native close notifications are queued. If the same dialog has already
      // reopened, this notification belongs to the previous close.
      if (!dialog.open) handleCloseEvent();
    };
    const dismiss = () => {
      // Native closing restores focus before its queued close event. Commit the
      // owner's closed state first, so an immediate shortcut sees that state.
      // This synchronous boundary is limited to native dismissal intentions.
      flushSync(() => handleCloseEvent());
    };
    const handleCancel = (event: Event) => {
      // The controlled effect performs the native close. Prevent the browser's
      // later default action from closing a dialog reopened during focus restore.
      event.preventDefault();
      dismiss();
    };
    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      const insideDialog =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;
      if (!insideDialog) dismiss();
    };

    dialog.addEventListener('close', handleClose);
    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleBackdropClick);
    return () => {
      dialog.removeEventListener('close', handleClose);
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleBackdropClick);
    };
  }, []);

  // Keep native visibility aligned during the commit, before another queued
  // native close can observe new controlled state with an old closed element.
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      const initialFocus =
        dialog.querySelector<HTMLElement>('[autofocus]') ??
        dialog.querySelector<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
            'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
      (initialFocus ?? dialog).focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      tabIndex={-1}
      className={`rd-dialog${className ? ` ${className}` : ''}`}
      aria-labelledby={labelledBy}
    >
      {children}
    </dialog>
  );
}
